export type BroadcastVariantMediaType = 'text' | 'audio';

export interface BroadcastMessageVariantUi {
  id: string;
  media_type: BroadcastVariantMediaType;
  content: string;
  media_url?: string;
  media_mime_type?: string;
  /** Local recording not yet uploaded (create/edit submit). */
  audioBlob?: Blob;
  /** Object URL for preview; revoke when variant removed. */
  audioPreviewUrl?: string;
}

export interface BroadcastMessageVariantPayload {
  media_type: BroadcastVariantMediaType;
  content: string;
  media_url?: string;
  media_mime_type?: string;
  audio_base64?: string;
}

function newVariantId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createEmptyBroadcastVariant(): BroadcastMessageVariantUi {
  return {
    id: newVariantId(),
    media_type: 'text',
    content: '',
  };
}

export function broadcastVariantFromApiMessage(msg: {
  content: string;
  media_type?: string;
  media_url?: string | null;
  media_mime_type?: string | null;
}): BroadcastMessageVariantUi {
  const mediaType = msg.media_type === 'audio' ? 'audio' : 'text';
  return {
    id: newVariantId(),
    media_type: mediaType,
    content: msg.content ?? '',
    ...(msg.media_url ? { media_url: msg.media_url } : {}),
    ...(msg.media_mime_type ? { media_mime_type: msg.media_mime_type } : {}),
    ...(mediaType === 'audio' && msg.media_url ? { audioPreviewUrl: msg.media_url } : {}),
  };
}

export function isBroadcastVariantValid(v: BroadcastMessageVariantUi): boolean {
  if (v.media_type === 'audio')
    return !!v.audioBlob || !!v.media_url?.trim();
  return v.content.trim().length > 0;
}

export function revokeVariantPreviewUrl(v: BroadcastMessageVariantUi): void {
  if (v.audioPreviewUrl?.startsWith('blob:'))
    URL.revokeObjectURL(v.audioPreviewUrl);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Same WEBM→WAV path as chat before upload. */
async function convertWebmToWav(webmBlob: Blob): Promise<Blob> {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const arrayBuffer = await webmBlob.arrayBuffer();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  const numOfChan = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numOfChan * bytesPerSample;
  const dataSize = audioBuffer.length * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;
  const wavBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(wavBuffer);

  const writeString = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numOfChan, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let i = 0; i < numOfChan; i++) channels.push(audioBuffer.getChannelData(i));
  let offset = 44;
  for (let i = 0; i < audioBuffer.length; i++) {
    for (let ch = 0; ch < numOfChan; ch++) {
      const sample = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([wavBuffer], { type: 'audio/wav' });
}

export async function prepareBroadcastVariantsForApi(
  variants: BroadcastMessageVariantUi[]
): Promise<BroadcastMessageVariantPayload[]> {
  const valid = variants.filter(isBroadcastVariantValid);
  const out: BroadcastMessageVariantPayload[] = [];

  for (const v of valid) {
    if (v.media_type === 'audio') {
      if (v.audioBlob) {
        const wav = await convertWebmToWav(v.audioBlob);
        const audio_base64 = await blobToBase64(wav);
        out.push({
          media_type: 'audio',
          content: v.content.trim(),
          media_mime_type: 'audio/wav',
          audio_base64,
        });
      } else {
        out.push({
          media_type: 'audio',
          content: v.content.trim(),
          media_url: v.media_url,
          media_mime_type: v.media_mime_type || 'audio/wav',
        });
      }
    } else {
      out.push({
        media_type: 'text',
        content: v.content.trim(),
      });
    }
  }

  return out;
}

export function applyAiOptimizeToFirstTextVariant(
  variants: BroadcastMessageVariantUi[],
  optimized: string
): BroadcastMessageVariantUi[] {
  if (variants.length === 0) return variants;
  const first = variants[0];
  if (first.media_type !== 'text') return variants;
  return [{ ...first, content: optimized }, ...variants.slice(1)];
}
